import { Form, Formik } from 'formik';
import React from 'react';
import { useParams } from 'react-router-dom';
import { Column, Columns } from 'shared/Columns';
import { changeSubmissionRoute, updateFirm } from 'utils/api/firms';
import { Admins, CommissionTerms, Contacts, DDStatus, FirmDetails, Packager, TermsOfBusiness } from '../partials';
import { FIELDS, FIELDS_LABELS } from '../types';
import { createBody } from '../utils';
import { assignCommission, unassignCommission } from './api';
import { FirmTypes, Props } from './types';
import { mapInitialData, mapResources } from './utils';
import validationSchema from './validationSchema';

const CmFirmProfile: React.FC<Props> = ({ data, onUpdate }) => {
  const { id: firmId } = useParams<{ id: string }>();
  const fetchedData = data;
  const initialData = mapInitialData(fetchedData);
  const resources = mapResources(fetchedData.resources);
  const firmType = fetchedData.company.firmType;
  const shouldCTBeDisabled = fetchedData.company.hasClub || firmType === FirmTypes.AR;

  const DIRECT_SUBMISSION_ROUTE_ID = '-1';

  const handleSubmissionRouteChangeClick = async (submissionRoute: string) => {
    await changeSubmissionRoute(
      firmId,
      submissionRoute === DIRECT_SUBMISSION_ROUTE_ID ? null : submissionRoute,
      submissionRoute === DIRECT_SUBMISSION_ROUTE_ID ? 'DIRECT' : 'CLUB',
    );
    onUpdate();
  };

  const handleCompanyChangeClick = (fieldName: string) => (values: any) => {
    return updateFirm(firmId, createBody(fieldName, values)).then(() => onUpdate());
  };
  const handleAdminChangeClick = (adminId: string) => (fieldName: string) => (values: any) => {
    return Promise.resolve();
  };

  const assignCommissionTerm = (setFieldError: any, commissionTerms: Array<string>) => (index: number) => async (
    commissionTermId: string,
    previousCommissionId: string,
  ) => {
    const filteredCommTermsArray = commissionTerms.filter((ct, ctIndex) => ct !== '' && ctIndex !== index);

    if (filteredCommTermsArray.includes(commissionTermId)) {
      setFieldError(`commissionTerms.${index}`, 'This commission firm has been already applied');
      return false;
    }

    if (previousCommissionId && !filteredCommTermsArray.includes(previousCommissionId)) {
      unassignCommission(firmId, previousCommissionId);
    }
    try {
      await assignCommission(firmId, commissionTermId);
      return true;
    } catch (e) {
      setFieldError(
        `commissionTerms.${index}`,
        e.response.data ? e.response.data.details : 'Cannot assign commission term.',
      );
    }
    return false;
  };

  const unassignCommissionTerm = (commissionTermId: string) => {
    unassignCommission(firmId, commissionTermId);
  };

  return (
    <>
      <Formik
        onSubmit={() => { }}
        initialValues={initialData}
        validationSchema={validationSchema}
        enableReinitialize={true}>
        {({ values, setFieldValue, setFieldError, initialValues }) => (
          <Form>
            <>
              <Columns>
                <Column>
                  <FirmDetails
                    firmType={firmType}
                    submissionRoutes={resources.submissionRoutes}
                    onChangeClick={handleSubmissionRouteChangeClick}
                    onCompanyChangeClick={handleCompanyChangeClick}
                  />
                  <CommissionTerms
                    title="Standard Commission terms"
                    singleItemTitle={firmType !== FirmTypes.AR ? FIELDS_LABELS.STANDARD_COMMISSION_TERM : undefined}
                    fieldName={FIELDS.STANDARD_COMMISSION_TERMS}
                    commissionTerms={resources.standardCommissionTerms}
                    assignCommissionTerm={assignCommissionTerm(setFieldError, values.standardCommissionTerms)}
                    unassignCommissionTerm={unassignCommissionTerm}
                    shouldBeDisabled={shouldCTBeDisabled}
                    onUpdate={onUpdate}
                  />
                  <CommissionTerms
                    title="Enhanced up-front Commission terms"
                    singleItemTitle={FIELDS_LABELS.ENHANCED_UPFRONT_COMMISSION_TERM}
                    fieldName={FIELDS.ENHANCED_UPFRONT_COMMISSION_TERMS}
                    commissionTerms={resources.enhancedCommissionTerms}
                    assignCommissionTerm={assignCommissionTerm(setFieldError, values.enhancedUpfrontCommissionTerms)}
                    unassignCommissionTerm={unassignCommissionTerm}
                    shouldBeDisabled={shouldCTBeDisabled}
                    onUpdate={onUpdate}
                    canDeleteEveryItem={true}
                  />
                  <CommissionTerms
                    title="Ongoing Commission terms"
                    singleItemTitle={FIELDS_LABELS.ONGOING_COMMISSION_TERM}
                    fieldName={FIELDS.ONGOING_COMMISSION_TERMS}
                    commissionTerms={resources.ongoingCommissionTerms}
                    assignCommissionTerm={assignCommissionTerm(setFieldError, values.ongoingCommissionTerms)}
                    unassignCommissionTerm={unassignCommissionTerm}
                    shouldBeDisabled={shouldCTBeDisabled}
                    onUpdate={onUpdate}
                    canDeleteEveryItem={true}
                  />
                  {firmType !== FirmTypes.AR && (
                    <TermsOfBusiness title="Terms of business" termsOfBusinessUrl={initialData.tobUrl} />
                  )}
                </Column>
                <Column>
                  <Admins
                    title="Firm Admins"
                    firmId={firmId}
                    firmName={initialData.firmName}
                    admins={values[FIELDS.ADMINS]}
                    onChangeClick={handleAdminChangeClick}
                    setFieldValue={setFieldValue}
                  />
                  <DDStatus
                    label={FIELDS_LABELS.DD_STATUS_FIRM}
                    data={{
                      fcaStatus: values[FIELDS.FCA_STATUS],
                      lmcStatus: values[FIELDS.LMC_STATUS],
                      dueDiligenceStatus: values[FIELDS.DD_STATUS],
                    }}
                    onChangeClick={handleCompanyChangeClick}
                  />
                  <Contacts
                    onChangeClick={handleCompanyChangeClick}
                    addressValues={values.address}
                    setFieldValue={setFieldValue}
                  />
                  <Packager
                    title="Packager"
                    initialData={{ isPackager: initialValues[FIELDS.IS_PACKAGER] }}
                    data={{ isPackager: values[FIELDS.IS_PACKAGER] }}
                    onChangeClick={handleCompanyChangeClick}
                  />
                </Column>
              </Columns>
            </>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default CmFirmProfile;
